/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useMemo } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles, @atlaskit/ui-styling-standard/use-compiled, @typescript-eslint/consistent-type-imports
import { css, Global, jsx } from '@emotion/react';
import { useIntl } from 'react-intl';

import { layoutMessages as messages } from '@atlaskit/editor-common/messages';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { token } from '@atlaskit/tokens';

const PLACEHOLDER_SELECTOR =
	'.ProseMirror-focused .layoutSectionView-content-wrap.selected [data-layout-column] > [data-layout-content] > p:only-child:has(.ProseMirror-trailingBreak:only-child)';

const layoutColumnDangerPreviewStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'.ProseMirror [data-layout-column].layout-column-danger-preview': {
		backgroundColor: token('color.background.danger'),
		boxShadow: `inset 0 0 0 2px ${token('color.border.danger')}`,
	},
});

const getPlaceholderStyle = (message: string) => {
	if (editorExperiment('platform_editor_controls', 'variant1')) {
		return css({
			// when paragraph is the only child, and it only has a trailingBreak.
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			[`${PLACEHOLDER_SELECTOR}`]: {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
				'&::before': {
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
					content: `"${message}"`,
					position: 'absolute',
					color: token('color.text.subtlest'),
					pointerEvents: 'none',
				},
			},
		});
	}

	return css({
		// when paragraph is the only child, and it only has a trailingBreak.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'.ProseMirror .layoutSectionView-content-wrap.selected [data-layout-column] > [data-layout-content] > p:only-child:has(.ProseMirror-trailingBreak:only-child)':
			{
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
				'&::before': {
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
					content: `"${message}"`,
					position: 'absolute',
					color: token('color.text.disabled'),
					font: token('font.body'),
					marginTop: token('space.050'),
					pointerEvents: 'none',
				},
			},
	});
};

export const GlobalStylesWrapper = (): jsx.JSX.Element => {
	const { formatMessage } = useIntl();

	const placeholderStyle = useMemo(() => {
		const placeholderText = editorExperiment('platform_editor_controls', 'variant1')
			? messages.controlslayoutPlaceholder
			: messages.layoutPlaceholder;
		return getPlaceholderStyle(formatMessage(placeholderText));
	}, [formatMessage]);

	const globalStyles = useMemo(
		() => [placeholderStyle, layoutColumnDangerPreviewStyle],
		[placeholderStyle],
	);

	return <Global styles={globalStyles} />;
};
